import { COORDINATE_SYSTEM } from "@deck.gl/core";
import type {
    Feature,
    FeatureCollection,
} from "@deck.gl-community/editable-layers";
import { faSpiral } from "@fortawesome/sharp-regular-svg-icons";
import centroid from "@turf/centroid";
import { interpolateRdBu, quantile, scaleSequentialLog } from "d3";
import { color as d3Color } from "d3-color";
import { GeoJsonLayer, type Layer, ScatterplotLayer } from "deck.gl";
import { DataSurveyTypeEnum } from "../../../apiClient/generated";
import { MAP_EXTRA_CLUSTERING_LIMIT } from "../constants";

// Icon sprite mapping for the extra-data providers. Each provider gets its own
// named region in the atlas so future providers only need a new sprite + entry
// here — no changes to the layer itself. ChampionX is the first (and currently
// only) provider.
const EXTRA_DATA_ICON_SIZE = 144;

// Shared accent color for the extra-data layers (icon mark, shape outlines and
// cluster bubbles). Exported so the legend can match it.
export const EXTRA_DATA_NAVY = "#2C4A5A";

const extraDataIconMapping = {
    [DataSurveyTypeEnum.ChampionX]: {
        x: 0,
        y: 0,
        width: EXTRA_DATA_ICON_SIZE,
        height: EXTRA_DATA_ICON_SIZE,
        mask: false,
    },
};

/**
 * Builds the canvas icon atlas for extra-data providers.
 */
function buildExtraDataIconAtlas() {
    const providerCount = Object.keys(extraDataIconMapping).length;
    const canvas = document.createElement("canvas");
    canvas.width = EXTRA_DATA_ICON_SIZE * providerCount;
    canvas.height = EXTRA_DATA_ICON_SIZE;
    const ctx = canvas.getContext("2d");

    // Scale everything up by 4 (36x36 logical space per cell).
    ctx.scale(4, 4);

    // --- ChampionX: soft white disc with fa-spiral ---
    const NAVY = EXTRA_DATA_NAVY;
    const cx = 18;
    const cy = 14;
    const radius = 9;

    // Light disc background so the mark reads on any map tile.
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = "black";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 0.5, 0, Math.PI * 2);
    ctx.stroke();

    // The fa-spiral glyph, centered inside the disc.
    const [iconWidth, iconHeight, , , iconPath] = faSpiral.icon;
    const markSize = 12;
    ctx.save();
    ctx.translate(cx - markSize / 2, cy - markSize / 2);
    ctx.scale(markSize / iconWidth, markSize / iconHeight);
    ctx.fillStyle = NAVY;
    ctx.fill(new Path2D(iconPath as string));
    ctx.restore();

    return canvas;
}

// Cached: a fresh canvas per call makes deck.gl re-upload the icon texture.
let extraDataIconAtlasCache: HTMLCanvasElement | null = null;
export function extraDataIconAtlas() {
    if (!extraDataIconAtlasCache) {
        extraDataIconAtlasCache = buildExtraDataIconAtlas();
    }
    return extraDataIconAtlasCache;
}

export const ExtraDataLayer = (
    features: Feature[],
    visible: boolean,
    zoomLevel: number,
    onHover?: (hoverInfo: any) => void,
    layerId?: string,
    selectedId?: string,
    clusteredData?: Feature[],
): Layer[] => {
    const extraDataPoints = [];
    const extraDataShapes = [];

    for (const i of features) {
        if (i.geometry.type === "Polygon") {
            if (!selectedId || i.properties.id !== selectedId) {
                extraDataShapes.push(i);
            }
            const center = centroid(i.geometry);
            extraDataPoints.push({
                ...i,
                geometry: center.geometry,
            });
        } else {
            extraDataPoints.push(i);
        }
    }

    const showClusters = clusteredData && clusteredData.length > 0;

    const layers: Layer[] = [
        new GeoJsonLayer({
            id: `extra-data-shapes${layerId ? "-" + layerId : ""}`,
            data: { type: "FeatureCollection", features: extraDataShapes },
            stroked: true,
            pickable: true,
            autoHighlight: true,
            visible: visible && zoomLevel >= MAP_EXTRA_CLUSTERING_LIMIT,
            onHover,
            highlightColor: [255, 255, 255, 20],
            getFillColor: [44, 74, 90, 35],
            lineWidthUnits: "pixels",
            getLineWidth: 1.5,
            getLineColor: [44, 74, 90, 220],
            opacity: 1,
        }),
    ];

    if (showClusters) {
        layers.push(
            new GeoJsonLayer({
                id: `extra-data-clusters${layerId ? "-" + layerId : ""}`,
                data: {
                    type: "FeatureCollection",
                    features: clusteredData.filter((i) => i.properties.cluster),
                },
                pointType: "circle+text",
                filled: true,
                stroked: true,
                getFillColor: [255, 255, 255, 180],
                getLineColor: [44, 74, 90, 255],
                lineWidthUnits: "pixels",
                getLineWidth: 2,
                getPointRadius: 20,
                pointRadiusUnits: "pixels",
                getText: (i) => `${i.properties.point_count_abbreviated}`,
                getTextSize: 12,
                getTextColor: [44, 74, 90, 255],
                getTextAnchor: "middle",
                getTextAlignmentBaseline: "center",
                pickable: true,
                autoHighlight: true,
                visible,
                onHover,
                opacity: 1,
            }),
            new GeoJsonLayer({
                id: `extra-data-points${layerId ? "-" + layerId : ""}`,
                data: {
                    type: "FeatureCollection",
                    features: clusteredData.filter(
                        (i) => !i.properties.cluster,
                    ),
                },
                pointType: "icon",
                iconAtlas: extraDataIconAtlas(),
                getIconSize: 40,
                getIcon: (f) =>
                    f.properties?.dataType ?? DataSurveyTypeEnum.ChampionX,
                iconMapping: extraDataIconMapping,
                filled: true,
                stroked: true,
                pickable: true,
                autoHighlight: true,
                visible,
                onHover,
                opacity: 1,
            }),
        );
    } else {
        layers.push(
            new GeoJsonLayer({
                id: `extra-data-points${layerId ? "-" + layerId : ""}`,
                data: { type: "FeatureCollection", features: extraDataPoints },
                pointType: "icon",
                iconAtlas: extraDataIconAtlas(),
                getIconSize: 40,
                getIcon: (f) =>
                    f.properties?.dataType ?? DataSurveyTypeEnum.ChampionX,
                iconMapping: extraDataIconMapping,
                filled: true,
                stroked: true,
                pickable: true,
                autoHighlight: true,
                visible,
                onHover,
                opacity: 1,
            }),
        );
    }

    return layers;
};

/**
 * Builds a `getFillColor` accessor for ChampionX points: RdBu scaled over the
 * given property on a log scale with 5th/95th percentile clipping, reversed so
 * blue = low, red = high.
 */
export const championXFillColor = (
    features: Feature[],
    colorProperty: string = "ch4",
) => {
    const values = features
        .map((f) => f.properties?.[colorProperty])
        .filter((v): v is number => typeof v === "number" && v > 0)
        .sort((a, b) => a - b);

    const lower = Math.max(quantile(values, 0.05) as number, 1e-9);
    const upper = Math.max(quantile(values, 0.95) as number, 1e-9);
    const scale = scaleSequentialLog(interpolateRdBu)
        .domain([upper, lower])
        .clamp(true);

    return (f: Feature): [number, number, number, number] => {
        const v = f.properties?.[colorProperty];
        if (typeof v !== "number") return [160, 160, 160, 200];
        const { r, g, b } = d3Color(scale(v))!.rgb();
        return [r, g, b, 200];
    };
};

export const ChampionXLayer = (
    data: FeatureCollection,
    visible: boolean,
    colorProperty: string = "ch4",
    layerId?: string,
    onHover?: (hoverInfo: any) => void,
) => {
    return [
        new GeoJsonLayer({
            id: `extra-data-points${layerId ? "-" + layerId : ""}`,
            data,
            filled: true,
            stroked: true,
            pickable: false,
            autoHighlight: true,
            visible,
            getFillColor: championXFillColor(data.features, colorProperty),
            getPointRadius: 30,
            pointRadiusMinPixels: 3,
            updateTriggers: { getFillColor: [colorProperty] },
            onHover,
        }),
    ];
};

/**
 * 3D point cloud of ChampionX survey measurements, colored the same way as
 * {@link ChampionXLayer}. Points are plotted at their coordinates, so the z
 * (3rd coordinate) must already be set to the desired height.
 */
export const ChampionX3DPointsLayer = (
    points: Feature[],
    colorProperty: string = "ch4",
): Layer[] => {
    return [
        new ScatterplotLayer({
            id: "championx-3d-points",
            data: points,
            coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
            getPosition: (f: Feature) => (f.geometry as any).coordinates,
            getFillColor: championXFillColor(points, colorProperty),
            getRadius: 3,
            radiusUnits: "pixels",
            radiusMinPixels: 2,
            stroked: true,
            getLineColor: [255, 255, 255, 120],
            lineWidthUnits: "pixels",
            getLineWidth: 0.5,
            pickable: true,
            autoHighlight: true,
            highlightColor: [255, 255, 255, 200],
            updateTriggers: { getFillColor: [colorProperty] },
        }),
    ];
};
